/**
 * Investigation runner — end-to-end wiring for the investigation pipeline.
 *
 * This module bridges the UI (investigation panel, focal points, news feed)
 * with the investigation orchestrator. It:
 *
 * 1. Listens for `investigation:request` events
 * 2. Creates investigations via `startNewInvestigation()`
 * 3. Shows decomposition confirmation via the investigation panel
 * 4. After confirmation, runs the full pipeline:
 *    COLLECT → EXTRACT → VALIDATE → GRAPH_BUILD → CORROBORATE → SYNTHESIZE → REVIEW
 * 5. Handles errors gracefully at every step
 * 6. Loads desk configs at boot
 *
 * AI NEVER controls workflow — the runner is deterministic orchestration code.
 */

import { eventBus } from '@/core/events';
import { appStore } from '@/core/store';
import {
  loadDeskConfig,
  setActiveDeskConfig,
  getActiveDeskConfigId,
  getDeskConfigIds,
} from '@/config/desk-config';
import type { DeskConfigJSON } from '@/config/desk-config';
import { getInvestigationPanel } from '@/panels/investigation-panel';
import type { InvestigationOrchestrator } from './orchestrator';
import { startNewInvestigation } from './orchestrator';
import type { InvestigationTierName, ScopeContract } from './types';
import { getInvestigation, updateInvestigation } from './investigation-store';
import { setDemoMode } from '@/intelligence/ai/provider-registry';
import { isDemoQuery, runDemoScenario, runDemoScenarioAfterConfirm, getDemoInvestigation, DEMO_SCOPE } from './demo-scenario';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

/** Currently active orchestrator (only one investigation runs at a time). */
let activeOrchestrator: InvestigationOrchestrator | null = null;

/** Cleanup functions for event subscriptions. */
const cleanupFns: (() => void)[] = [];

// ---------------------------------------------------------------------------
// Desk config loading
// ---------------------------------------------------------------------------

/** Built-in desk config JSON files (imported statically for reliability). */
const DESK_CONFIG_PATHS: Record<string, () => Promise<{ default: DeskConfigJSON }>> = {
  'baltic-maritime': () => import('../../../desk-configs/baltic-maritime.json').then(m => ({ default: m.default as unknown as DeskConfigJSON })),
  'cyber-operations': () => import('../../../desk-configs/cyber-operations.json').then(m => ({ default: m.default as unknown as DeskConfigJSON })),
};

/**
 * Load all desk config JSON files from desk-configs/ directory.
 * Called once at boot.
 */
async function loadAllDeskConfigs(): Promise<void> {
  for (const [id, loader] of Object.entries(DESK_CONFIG_PATHS)) {
    try {
      const mod = await loader();
      loadDeskConfig(mod.default);
      console.log(`[Sentinel] Loaded desk config: ${id}`);
    } catch (err) {
      console.warn(`[Sentinel] Failed to load desk config ${id}:`, err);
    }
  }

  // Set active desk config based on current desk preset
  const configIds = getDeskConfigIds();
  if (configIds.length > 0 && !getActiveDeskConfigId()) {
    // Try to match current desk preset
    const currentVariant = appStore.getState().variant;
    const matchingConfig = configIds.find(id => currentVariant.includes(id) || id.includes(currentVariant));
    if (matchingConfig) {
      setActiveDeskConfig(matchingConfig);
    } else {
      // Default to first available
      setActiveDeskConfig(configIds[0]);
    }
  }
}

/**
 * Get the desk config ID to use for investigations.
 * Falls back to the first available desk config.
 */
function getInvestigationDeskId(): string {
  const activeId = getActiveDeskConfigId();
  if (activeId) return activeId;

  const ids = getDeskConfigIds();
  if (ids.length > 0) return ids[0];

  throw new Error('No desk configs loaded. Cannot start investigation.');
}

// ---------------------------------------------------------------------------
// Investigation pipeline runner
// ---------------------------------------------------------------------------

/**
 * Run the full investigation pipeline after scope confirmation.
 *
 * Flow:
 * COLLECT (progressive: collect + extract + validate)
 * → GRAPH_BUILD (relationship identification)
 * → CORROBORATE (NATO ratings)
 * → SYNTHESIZE (if tier supports it)
 * → REVIEW
 *
 * Each step catches errors independently so the pipeline continues
 * with partial results rather than crashing entirely.
 */
async function runPipeline(orchestrator: InvestigationOrchestrator): Promise<void> {
  try {
    // Phase 1: Progressive collection + extraction
    // This transitions through COLLECT → EXTRACT → VALIDATE
    await orchestrator.runCollectionAndExtraction();

    // Check if we were forced to review (resource limits)
    if (orchestrator.getState() === 'review' || orchestrator.getState() === 'error') {
      return;
    }

    // Phase 2: Build graph (relationship identification)
    await orchestrator.buildGraph();
    if (orchestrator.getState() === 'error') return;

    // Phase 3: Corroboration pass (NATO ratings)
    await orchestrator.corroborate();
    if (orchestrator.getState() === 'error') return;

    // Phase 4: Synthesis (draft assessment — Opus)
    await orchestrator.synthesize();
    if (orchestrator.getState() === 'error') return;

    // Phase 5: Transition to review
    await orchestrator.completeToReview();

  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.error('[Sentinel] Pipeline error:', message);

    // If we extracted any entities, force to review with partial results
    // rather than losing all work
    try {
      if (orchestrator.getState() !== 'review' && orchestrator.getState() !== 'error') {
        await orchestrator.forceReview(`Pipeline error: ${message}`);
      }
    } catch {
      // Last resort: transition to error
      try {
        await orchestrator.transitionToError(`Fatal pipeline error: ${message}`);
      } catch {
        // Nothing more we can do
      }
    }
  } finally {
    activeOrchestrator = null;
  }
}

// ---------------------------------------------------------------------------
// Handle investigation requests
// ---------------------------------------------------------------------------

/**
 * Handle an `investigation:request` event.
 * Creates the investigation, runs decomposition, shows confirmation UI,
 * then runs the full pipeline after analyst confirms.
 */
async function handleInvestigationRequest(
  query: string,
  tier: InvestigationTierName,
): Promise<void> {
  // Prevent concurrent investigations
  if (activeOrchestrator) {
    console.warn('[Sentinel] Investigation already in progress. Ignoring request.');
    return;
  }

  // Handle special synthesis re-run request from graph panel
  if (query.startsWith('__synthesize:')) {
    const investigationId = query.replace('__synthesize:', '');
    await handleSynthesisRerun(investigationId);
    return;
  }

  // Demo scenario — full pipeline showcase with realistic timing.
  // Check BEFORE resolving deskId so the demo works even when
  // desk configs haven't loaded yet (race condition on boot).
  if (isDemoQuery(query)) {
    setDemoMode(true);
    try {
      // Run decomposition phase (creates investigation, returns scope)
      await runDemoScenario(query, tier);

      const investigation = getDemoInvestigation();
      if (!investigation) throw new Error('Demo investigation not created');

      // Show confirmation UI — same UX as real pipeline
      const panel = getInvestigationPanel();
      panel.showDecompositionConfirmation(
        investigation,
        DEMO_SCOPE,
        // onConfirm — run rest of demo pipeline
        async () => {
          try {
            await runDemoScenarioAfterConfirm(investigation.id);
          } catch (err) {
            console.error('[Sentinel] Demo scenario error:', err);
            eventBus.emit('investigation:error', {
              investigationId: investigation.id,
              error: err instanceof Error ? err.message : String(err),
              state: 'collect' as import('./types').InvestigationState,
            });
          } finally {
            setDemoMode(false);
          }
        },
        // onCancel
        async () => {
          try {
            await updateInvestigation(investigation.id, { state: 'error' });
          } catch { /* ignore if DB not ready */ }
          eventBus.emit('investigation:error', {
            investigationId: investigation.id,
            error: 'Investigation cancelled by analyst',
            state: 'decompose' as import('./types').InvestigationState,
          });
          setDemoMode(false);
        },
      );

      return;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error('[Sentinel] Demo scenario failed:', message);
      setDemoMode(false);
      // Emit error so the investigation panel resets from loading state
      eventBus.emit('investigation:error', {
        investigationId: 'demo',
        error: `Demo failed: ${message}`,
        state: 'init' as import('./types').InvestigationState,
      });
      return; // Don't fall through to normal pipeline — it will also fail
    }
  }

  let deskId: string;
  try {
    deskId = getInvestigationDeskId();
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.error('[Sentinel] No desk config available:', message);
    eventBus.emit('investigation:error', {
      investigationId: 'none',
      error: message,
      state: 'init' as import('./types').InvestigationState,
    });
    return;
  }

  try {
    // Start new investigation — this creates the DB record and runs decomposition
    const orchestrator = await startNewInvestigation(query, deskId, tier);
    activeOrchestrator = orchestrator;

    // Get the investigation record (now has decomposition result)
    const investigation = await orchestrator.getInvestigation();
    if (!investigation) {
      console.error('[Sentinel] Investigation not found after start');
      activeOrchestrator = null;
      return;
    }

    // Check if decomposition failed (state would be 'error')
    if (orchestrator.getState() === 'error') {
      activeOrchestrator = null;
      return;
    }

    // Show decomposition confirmation to analyst
    const scopeContract = investigation.decomposed as ScopeContract | null;
    if (!scopeContract) {
      console.error('[Sentinel] No decomposition result');
      await orchestrator.transitionToError('Query decomposition produced no scope contract');
      activeOrchestrator = null;
      return;
    }

    // Show confirmation UI — analyst must confirm or cancel
    const panel = getInvestigationPanel();
    panel.showDecompositionConfirmation(
      investigation,
      scopeContract,
      // onConfirm
      async (editedScope: ScopeContract) => {
        try {
          // Analyst confirmed — lock scope and start collection
          await orchestrator.confirmScope(editedScope);

          // Run the rest of the pipeline
          await runPipeline(orchestrator);

        } catch (err) {
          const errMsg = err instanceof Error ? err.message : String(err);
          console.error('[Sentinel] Investigation failed after confirmation:', errMsg);
          try {
            await orchestrator.transitionToError(errMsg);
          } catch { /* ignore */ }
          activeOrchestrator = null;
        }
      },
      // onCancel
      async () => {
        try {
          await orchestrator.transitionToError('Investigation cancelled by analyst');
        } catch { /* ignore */ }
        activeOrchestrator = null;
      },
    );

  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.error('[Sentinel] Failed to start investigation:', message);
    eventBus.emit('investigation:error', {
      investigationId: 'none',
      error: message,
      state: 'init' as import('./types').InvestigationState,
    });
    activeOrchestrator = null;
  }
}

/**
 * Handle a synthesis re-run for an existing investigation.
 * Called when the graph panel's "Generate Assessment" button is clicked.
 */
async function handleSynthesisRerun(investigationId: string): Promise<void> {
  const investigation = await getInvestigation(investigationId);
  if (!investigation) {
    console.error('[Sentinel] Investigation not found for synthesis re-run:', investigationId);
    return;
  }

  // Import and run synthesis directly (investigation already has entities + graph)
  try {
    const { TokenTracker, createLLMAdapter } = await import('@/intelligence/ai/llm-adapter');
    const { runSynthesis } = await import('@/intelligence/ai/report-writer');
    const { entityStore } = await import('@/intelligence/entities/entity-store');
    const { getInvestigationEntityIds } = await import('./investigation-store');
    const { getDeskConfig: getDC } = await import('@/config/desk-config');

    const deskConfig = getDC(investigation.deskId);
    if (!deskConfig) {
      console.error('[Sentinel] Desk config not found for synthesis');
      return;
    }

    const tokenTracker = new TokenTracker();
    const adapter = createLLMAdapter(tokenTracker);

    const entityIds = await getInvestigationEntityIds(investigationId);
    const entities: import('@/intelligence/entities/types').Entity[] = [];
    for (const eid of entityIds) {
      const e = await entityStore.getById(eid);
      if (e) entities.push(e);
    }

    const allRelationships: import('@/intelligence/entities/types').Relationship[] = [];
    const seenRelIds = new Set<string>();
    for (const entity of entities) {
      const rels = await entityStore.getRelationships(entity.id);
      for (const rel of rels) {
        if (!seenRelIds.has(rel.id)) {
          seenRelIds.add(rel.id);
          allRelationships.push(rel);
        }
      }
    }

    const assessment = await runSynthesis(
      adapter,
      investigationId,
      entities,
      allRelationships,
      investigation.coverage,
      deskConfig,
    );

    eventBus.emit('investigation:synthesis-complete', {
      investigationId,
      assessment,
    });

  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.error('[Sentinel] Synthesis re-run failed:', message);
  }
}

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

/**
 * Initialize the investigation runner.
 * Called once from main.ts during boot.
 *
 * 1. Loads desk configs from JSON
 * 2. Listens for investigation:request events
 * 3. Returns cleanup function for teardown
 */
export async function initInvestigationRunner(): Promise<() => void> {
  // Load desk configs
  await loadAllDeskConfigs();

  // Listen for investigation requests from UI
  const unsubRequest = eventBus.on('investigation:request', ({ query, tier }) => {
    handleInvestigationRequest(query, tier).catch(err => {
      console.error('[Sentinel] Investigation request handler error:', err);
    });
  });
  cleanupFns.push(unsubRequest);

  // Listen for desk changes — update active desk config
  const unsubVariant = eventBus.on('variant:changed', ({ to }) => {
    const configIds = getDeskConfigIds();
    const match = configIds.find(id => to.includes(id) || id.includes(to));
    if (match) {
      try {
        setActiveDeskConfig(match);
      } catch { /* desk config not loaded for this variant */ }
    }
  });
  cleanupFns.push(unsubVariant);

  console.log('[Sentinel] Investigation runner initialized');

  return () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
    activeOrchestrator = null;
  };
}

/**
 * Check if an investigation is currently running.
 */
export function isInvestigationRunning(): boolean {
  return activeOrchestrator !== null;
}

/**
 * Get the active orchestrator (if any).
 */
export function getActiveOrchestrator(): InvestigationOrchestrator | null {
  return activeOrchestrator;
}
